import MapAddress from "@/components/address";
import { LocationDto } from "@/models/club";
import { updateMerchantAddressPayload } from "@/models/payload/profile";
import { Select, Skeleton, Textarea, TextInput } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import "leaflet/dist/leaflet.css";
import { useEffect } from "react";
import * as Yup from "yup";
import { IRAN_PROVINCES } from "@/modules/profile/iranProvinces";
import SettingsShell, { SettingsSection } from "@/modules/settings/SettingsShell";
import classes from "@/modules/settings/settings.module.css";
import useGetMerchant from "../../hooks/useGetMerchant";
import useUpdateMerchantAddress from "../../hooks/useUpdateMerchantAddress";

const schema = Yup.object({
  address: Yup.string().required("آدرس را وارد کنید یا روی نقشه انتخاب کنید."),
  postalCode: Yup.string().test("postal", "کد پستی ۱۰ رقم است", (v) => !v || /^\d{10}$/.test(v)),
});
const TEHRAN = { lat: 35.7219, lon: 51.3347 };

type Values = {
  address: string;
  addressDetail: string;
  location?: LocationDto;
  province: string;
  city: string;
  district: string;
  postalCode: string;
};

// Address: one map to place the pin (it fills the address line), the address text, and the structured parts
// Google and maps use. Edited on the page — the old page showed a second, read-only map that sat above the
// edit dialog.
function EditProfileAddress() {
  const { data, isLoading, refetch } = useGetMerchant();
  const { updateMerchant, isLoading: saving } = useUpdateMerchantAddress();

  const initial = (): Values => ({
    address: data?.address ?? "",
    addressDetail: data?.addressDetail ?? "",
    location: data?.location?.lat && data?.location?.lon ? { lat: data.location.lat, lon: data.location.lon } : undefined,
    province: data?.province ?? "",
    city: data?.city ?? "",
    district: data?.district ?? "",
    postalCode: data?.postalCode ?? "",
  });
  const form = useForm<Values>({ validate: yupResolver(schema), initialValues: initial() });
  useEffect(() => {
    const values = initial();
    form.setValues(values);
    form.resetDirty(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const submit = (values: Values) => {
    if (!values.location) {
      notifications.show({ message: "محل سالن را روی نقشه مشخص کنید.", color: "yellow" });
      return;
    }
    updateMerchant(values as updateMerchantAddressPayload, {
      onSuccess() { notifications.show({ message: "آدرس ذخیره شد", color: "green" }); form.resetDirty(); refetch(); },
    });
  };

  return (
    <SettingsShell title="آدرس" description="محل سالن روی نقشه و آدرسی که مشتری‌ها می‌بینند." onSubmit={form.onSubmit(submit)} dirty={form.isDirty()} saving={saving} onReset={() => form.reset()}>
      {isLoading ? (
        <><Skeleton height={340} radius={14} /><Skeleton height={160} radius={14} /></>
      ) : (
        <>
          <SettingsSection title="محل روی نقشه" description="نشانگر را روی در ورودی سالن بگذارید؛ آدرس از روی آن پر می‌شود و قابل ویرایش است.">
            <div className={classes.map}>
              <MapAddress
                latLng={(form.values.location ?? TEHRAN) as LocationDto}
                center={data?.location?.lat ? data.location : TEHRAN}
                setLocation={(e) => form.setFieldValue("location", { lat: e.lat, lon: e.lon })}
                // The map looks up the address of its centre on every load; only a moved pin replaces the saved text.
                setAddress={(e) => { if (form.isDirty("location") || !form.values.address) form.setFieldValue("address", e); }}
              />
            </div>
          </SettingsSection>

          <SettingsSection title="آدرس" description="همان‌طور که روی سایت و در پیامک تأیید نوبت نوشته می‌شود.">
            <TextInput label="آدرس" withAsterisk placeholder="تهران، سعادت‌آباد، بلوار پاکنژاد" {...form.getInputProps("address")} />
            <Textarea label="جزئیات" placeholder="پلاک، طبقه، واحد…" autosize minRows={2} maxRows={4} {...form.getInputProps("addressDetail")} />
          </SettingsSection>

          <SettingsSection title="استان، شهر و محله" description="به گوگل و نقشه‌ها کمک می‌کند سالن را در جستجوی محلی نشان دهند.">
            <div className={classes.fields}>
              <Select label="استان" placeholder="انتخاب استان" searchable clearable data={IRAN_PROVINCES} nothingFoundMessage="پیدا نشد" {...form.getInputProps("province")} onChange={(v) => form.setFieldValue("province", v ?? "")} />
              <TextInput label="شهر" placeholder="مثلاً تهران" maxLength={100} {...form.getInputProps("city")} />
              <TextInput label="محله یا منطقه" placeholder="مثلاً سعادت‌آباد" maxLength={100} {...form.getInputProps("district")} />
              <TextInput label="کد پستی" placeholder="۱۰ رقم" maxLength={10} styles={{ input: { direction: "ltr", textAlign: "left" } }} {...form.getInputProps("postalCode")} />
            </div>
          </SettingsSection>
        </>
      )}
    </SettingsShell>
  );
}

export default EditProfileAddress;
